/**
 * STEEL ↔ CONCRETE BASE-CONNECTION model — the interface that sits *between* the
 * tower steel (legs / monopole shaft / guy) and the concrete foundation modelled
 * in `foundation.ts`. The foundation module answers "can the *ground* hold this
 * footing?"; this module answers "can the *steel-to-concrete joint* transfer the
 * load into that footing before the bolts, the plate, or the concrete let go?".
 *
 * The two compose in series: a base node survives only if BOTH the connection
 * (here) and the foundation (there) are within capacity. The weaker governs.
 *
 * Units are SI throughout: metres, Newtons, Pascals, N·m. A few catalog fields
 * carry an imperial label in the name for provenance, but every number is SI.
 *
 * NOT wired into the solver — see docs/BASE_CONNECTION.md for the integration
 * points (a `connection?` field on FoundationSpec, and how a connection failure
 * feeds the same "release the pinned node" mechanic foundation.ts already uses,
 * either → "Fall Over" (leg detaches) or "Get Sued" (anchor/guy tears out)).
 *
 * Standalone by design: no imports needed (kept decoupled from model.ts so it
 * can be unit-tested and reused). Steel yield values mirror model.ts MATERIALS
 * (A36 250 MPa, A572 345 MPa) but connection hardware has its *own* grades
 * (F1554, A615, A449) so we carry them locally rather than import.
 *
 * Sources (drawings/reports in reference_towers/, cited inline):
 *  - [DS200] Valmont RTS DS200 3-bolt anchor base drawing: 3/4" F1554 Gr.55
 *    anchor bolts, 8" bolt circle, 17" embedment, 3" hook, A36 base plate.
 *  - [SABRE] Sabre Cane Ridge 195 ft monopole foundation drawings: 26 × 2.25"
 *    anchor rods on a 75" bolt circle, 9 ft pier, (62) #8 vertical rebar + #5
 *    ties in pier, (69) #9 rebar mat, A615 Gr.60, f'c per ACI, dev length in pad
 *    135 in provided vs 31 in required.
 *  - [NORWALK] CT Norwalk Valmont 150 ft monopole: (8) 1-3/4" F1554 Gr.105
 *    (Fy=105 ksi, Fu=125 ksi) on 49" BC; 30 #-bar pier cage, Grade 60.
 *  - [ACI] Telecom Tower RC Foundation, ACI 318-14 worked example: pier rebar
 *    cage 28–36 #6, Grade 60, 3" cover, f'c = 3000 psi. Breakout/development per
 *    ACI 318-14 Ch.17 (was App. D) and Ch.25 (development length).
 *  - [TowerNX] TowerNX monopole base design guide: AASHTO base-plate rule
 *    (t_plate ≥ bolt Ø, edge distance ≥ 1–2 bolt Ø), grouted/ungrouted bolt
 *    force distribution, base-plate cantilever bending M = T·arm.
 *  - [ACI-17] ACI 318-14 §17.4 concrete breakout: Ncb = (ANc/ANco)·ψ·Nb,
 *    Nb = kc·√f'c·hef^1.5 (SI kc ≈ 10 for cast-in, 7 for post-installed).
 *  - [F1554] ASTM F1554 anchor-rod grades 36 / 55 / 105 ksi yield.
 */

// ---------------------------------------------------------------------------
// physical constants (kept local so this module has no imports)
// ---------------------------------------------------------------------------

/** 1 inch in metres. */
const IN = 0.0254;
/** 1 ksi in Pascals (1000 psi). */
const KSI = 6.894757e6;

// ---------------------------------------------------------------------------
// Steel / rebar grades used in the *connection* (not the tower members)
// ---------------------------------------------------------------------------

/** A hardware steel grade: yield Fy and ultimate tensile Fu (Pa). */
export interface ConnSteelGrade {
  name: string;
  Fy: number; // yield strength (Pa)
  Fu: number; // ultimate tensile strength (Pa)
}

/**
 * Anchor-rod / bolt grades. Anchor-bolt design is governed by the *ultimate*
 * tensile strength Fu on the tensile-stress area (fracture), and by Fy for
 * serviceability; we carry both. [F1554][DS200][NORWALK]
 */
export const BOLT_GRADES = {
  // ASTM A36 rod — mild, common on small poles/lattice stubs.
  a36rod: { name: "A36 rod", Fy: 36 * KSI, Fu: 58 * KSI },
  // ASTM F1554 Grade 36 — the low-strength anchor-rod grade.
  f1554_36: { name: "F1554 Gr.36", Fy: 36 * KSI, Fu: 58 * KSI },
  // ASTM F1554 Grade 55 — the workhorse anchor rod. [DS200 3/4" Gr.55]
  f1554_55: { name: "F1554 Gr.55", Fy: 55 * KSI, Fu: 75 * KSI },
  // ASTM F1554 Grade 105 — high-strength monopole anchor rod. [NORWALK 1-3/4"]
  f1554_105: { name: "F1554 Gr.105", Fy: 105 * KSI, Fu: 125 * KSI },
  // ASTM A615 Gr.75 deformed bar used as anchor (Valmont S-23 exceeds this).
  a615_75: { name: "A615 Gr.75", Fy: 75 * KSI, Fu: 100 * KSI },
  // ASTM A449 high-strength bolt (guy anchor rods / eye rods).
  a449: { name: "A449", Fy: 92 * KSI, Fu: 120 * KSI },
} satisfies Record<string, ConnSteelGrade>;

export type BoltGradeKey = keyof typeof BOLT_GRADES;

/**
 * Reinforcing-bar (rebar) grade. fy for a pier/pad cage; ACI Grade 60 = 60 ksi
 * ≈ 420 MPa (the near-universal tower value), Grade 75 (520 MPa) for heavy work,
 * Fe-500 (500 MPa) in metric practice. [ACI/SABRE Grade 60][HM Fe-500]
 */
export interface RebarGrade {
  name: string;
  fy: number; // yield (Pa)
}

export const REBAR_GRADES = {
  g60: { name: "Grade 60 (420 MPa)", fy: 420e6 }, // [ACI/SABRE/NORWALK]
  fe500: { name: "Fe-500 (500 MPa)", fy: 500e6 }, // [HM]
  g75: { name: "Grade 75 (520 MPa)", fy: 520e6 },
} satisfies Record<string, RebarGrade>;

export type RebarGradeKey = keyof typeof REBAR_GRADES;

// ---------------------------------------------------------------------------
// Bolt-size catalog — real anchor-rod / bolt diameters with tensile-stress area
// ---------------------------------------------------------------------------

/**
 * A bolt / anchor-rod size. The load-bearing property is the *tensile stress
 * area* At (m^2) — the effective area through the threads, ~ π/4·(d − 0.9743/n)^2
 * for UNC; less than the nominal shank area A. Capacities use At for tension,
 * A (unthreaded shank) for shear. [AISC/ASTM tensile-stress-area tables]
 */
export interface BoltSize {
  name: string;
  d: number; // nominal diameter (m)
  A: number; // nominal shank area = π/4·d^2 (m^2)
  At: number; // tensile stress area through threads (m^2)
}

/** Build a BoltSize from an inch diameter and a tensile-stress area in in^2. */
function bolt(label: string, dIn: number, AtIn2: number): BoltSize {
  const d = dIn * IN;
  return {
    name: label,
    d,
    A: (Math.PI / 4) * d * d,
    At: AtIn2 * IN * IN,
  };
}

/**
 * Anchor-bolt / rod sizes, small → large. Tensile-stress areas from the AISC
 * UNC table. Covers the real range seen on the drawings: 3/4" on small poles
 * [DS200] up to 2-1/4" on the 195 ft monopole [SABRE].
 */
export const BOLT_SIZES = {
  b0_75: bolt('3/4"', 0.75, 0.334), // [DS200 anchor bolt]
  b0_875: bolt('7/8"', 0.875, 0.462),
  b1_0: bolt('1"', 1.0, 0.606),
  b1_25: bolt('1-1/4"', 1.25, 0.969),
  b1_5: bolt('1-1/2"', 1.5, 1.405),
  b1_75: bolt('1-3/4"', 1.75, 1.90), // [NORWALK 1-3/4" F1554-105]
  b2_0: bolt('2"', 2.0, 2.50),
  b2_25: bolt('2-1/4"', 2.25, 3.25), // [SABRE 2.25" anchor rod]
} satisfies Record<string, BoltSize>;

export type BoltSizeKey = keyof typeof BOLT_SIZES;

// ---------------------------------------------------------------------------
// Rebar-size catalog (US #-bars, SI areas)
// ---------------------------------------------------------------------------

/** A reinforcing bar size: nominal diameter db (m) and area Ab (m^2). */
export interface RebarSize {
  name: string;
  db: number; // bar diameter (m)
  Ab: number; // bar cross-section area (m^2)
}

/** Build a #-bar: US bar No. n has db = n/8 inch. */
function rebar(n: number): RebarSize {
  const db = (n / 8) * IN;
  return { name: `#${n}`, db, Ab: (Math.PI / 4) * db * db };
}

/**
 * Rebar sizes used in tower pier/pad cages. [ACI #6][NORWALK/SABRE #8–#9].
 * #8 ≈ 25 mm, #9 ≈ 29 mm, #11 ≈ 36 mm.
 */
export const REBAR_SIZES = {
  n5: rebar(5), // #5 ties [SABRE]
  n6: rebar(6), // #6 cage [ACI]
  n8: rebar(8), // #8 pier verticals [SABRE 62 #8]
  n9: rebar(9), // #9 mat [SABRE 69 #9]
  n11: rebar(11), // #11 heavy vertical
} satisfies Record<string, RebarSize>;

export type RebarSizeKey = keyof typeof REBAR_SIZES;

// ---------------------------------------------------------------------------
// Connection-spec interfaces (one per real connection type)
// ---------------------------------------------------------------------------

/**
 * (a) BASE PLATE + ANCHOR-BOLT group — the monopole / self-support foot.
 * The pole/leg welds to a thick steel base plate; the plate bolts down onto a
 * ring of cast-in anchor rods in the pier. Uplift + shear go through the bolts;
 * download bears through the plate onto the concrete/grout. [SABRE][NORWALK][DS200]
 */
export interface AnchorBoltGroup {
  kind: "anchorBolts";
  nBolts: number; // number of anchor rods in the circle
  bolt: BoltSizeKey; // rod size
  grade: BoltGradeKey; // rod grade (F1554/A36/A449...)
  boltCircleDia: number; // bolt-circle diameter (m) — lever arm for the couple
  embedment: number; // effective embedment depth hef (m) into concrete
  edgeDistance: number; // clear edge distance ca1 from bolt to concrete edge (m)
  platePlanArea: number; // base-plate bearing area on concrete/grout (m^2)
  plateThickness: number; // base-plate thickness (m) — governs plate bending
  plateGrade: BoltGradeKey; // base-plate steel grade (A36 typ.) [DS200]
  fc: number; // concrete f'c (Pa) the group is cast into
}

/**
 * (b) EMBEDDED STUB — a lattice leg (angle/pipe) cast directly into the pier/pad
 * with no base plate. Transfers axial load by *bond + bearing* over the embedded
 * length; the "stick-em-in-concrete" detail. [DS200 "designed with embedded"].
 * Failure is bond/pullout (like rebar development) or a concrete breakout cone.
 */
export interface EmbeddedStub {
  kind: "embeddedStub";
  perimeter: number; // bonded surface perimeter of the stub section (m)
  embedment: number; // embedded length hef (m)
  edgeDistance: number; // clear distance stub-to-concrete-edge (m)
  fc: number; // concrete f'c (Pa)
  bondStrength?: number; // ultimate bond stress u (Pa); default ~ from f'c
}

/**
 * (c) GUY-ANCHOR ROD / eye — the steel rod from the guy turnbuckle down into the
 * deadman or grouted rock anchor. Pure tension member; fails by rod fracture or
 * by the concrete/grout it's cast into breaking out. [A449/F1554 eye rods]
 */
export interface GuyAnchorRod {
  kind: "guyRod";
  rodDia: number; // rod diameter (m)
  grade: BoltGradeKey; // rod grade
  embedment: number; // bonded/hooked length into deadman/grout (m)
  edgeDistance: number; // edge distance in the block (m)
  fc: number; // deadman concrete / grout strength (Pa)
}

/**
 * (d) REBAR CAGE — vertical reinforcement in a pier, characterised by its total
 * steel ratio and grade. Not a discrete leg connection but the tension tie that
 * carries anchor-bolt uplift down into the pad; capacity = As·fy of the cage.
 */
export interface RebarCage {
  kind: "rebarCage";
  bar: RebarSizeKey; // bar size
  nBars: number; // number of vertical bars
  grade: RebarGradeKey; // rebar grade
  developmentLength: number; // provided development length ld (m)
  grossArea: number; // gross concrete section area (m^2) — for ρ reporting
}

/** The tagged union: a base node/anchor carries exactly one of these. */
export type BaseConnectionSpec =
  | AnchorBoltGroup
  | EmbeddedStub
  | GuyAnchorRod
  | RebarCage;

// ---------------------------------------------------------------------------
// Strength-reduction (φ) and safety factors — ACI 318 / TIA-222
// ---------------------------------------------------------------------------

/**
 * φ strength-reduction factors (ACI 318-14) and the game's target demand safety
 * factor. Capacities returned by the functions below are *nominal* (Nn); the
 * design capacity is φ·Nn, and we fail when demand·SF > φ·Nn.
 */
export const CONN_PHI = {
  boltTension: 0.75, // ACI 17.5.1.2 ductile steel tension
  boltShear: 0.65, // ACI 17.5.1.2 steel shear
  breakout: 0.65, // ACI 17.5.2.1 concrete breakout (Condition B)
  bond: 0.65, // ACI 17.5.5 adhesive/bond
  plate: 0.9, // AISC flexure of the base plate
  rebar: 0.9, // ACI 21.2 tension-controlled
} as const;

/** Target demand safety factor over φ·Nn (matches foundation.ts uplift ≈ 1.5–2). */
export const CONN_SAFETY_FACTOR = 1.5;

// ---------------------------------------------------------------------------
// Capacity functions — all return Newtons (nominal, pre-φ unless noted)
// ---------------------------------------------------------------------------

/**
 * ANCHOR-BOLT TENSION capacity of the whole group (N), nominal.
 *   Nsa = n · At · Fu           [ACI 17.4.1.2] (fracture through the threads)
 * We use Fu (ultimate) on the tensile-stress area, per ACI steel-strength rules.
 * Multiply by φ = 0.75 for design.
 */
export function anchorTensionCapacity(g: AnchorBoltGroup): number {
  const b = BOLT_SIZES[g.bolt];
  const grade = BOLT_GRADES[g.grade];
  return g.nBolts * b.At * grade.Fu;
}

/**
 * ANCHOR-BOLT SHEAR capacity of the whole group (N), nominal.
 *   Vsa = n · 0.6 · At · Fu     [ACI 17.5.1.2] (shear ≈ 0.6× tension)
 * The 0.6 is the shear-to-tension ratio for the steel. φ = 0.65 for design.
 */
export function anchorShearCapacity(g: AnchorBoltGroup): number {
  const b = BOLT_SIZES[g.bolt];
  const grade = BOLT_GRADES[g.grade];
  return g.nBolts * 0.6 * b.At * grade.Fu;
}

/**
 * CONCRETE BREAKOUT capacity in tension of the anchor group (N), nominal — the
 * cone of concrete that pulls out with the bolts if the concrete fails before
 * the steel. Simplified from ACI 318-14 §17.4.2:
 *
 *   Nb  = kc · √f'c · hef^1.5                    (single-anchor basic breakout)
 *   Ncbg = (ANc / ANco) · ψ_ec · ψ_ed · ψ_c · Nb (group)
 *
 * with ANco = 9·hef^2 (full cone), ANc the projected group area (capped by edge
 * distances), ψ_ed the edge-distance modifier. SI cast-in coefficient kc ≈ 10
 * (ACI 17.4.2.2, √f'c in MPa gives N; we keep everything in Pa and scale).
 *
 * We approximate the group's projected area as a square enclosing the bolt
 * circle expanded by 1.5·hef each side, clipped at the concrete edge — enough
 * fidelity for the game while preserving the real hef^1.5 sensitivity that makes
 * shallow embedment fail. Deeper embedment → much more breakout capacity.
 */
export function concreteBreakoutCapacity(g: AnchorBoltGroup): number {
  const hef = g.embedment;
  // kc for cast-in anchors in cracked concrete; SI form uses √f'c in MPa → N.
  // Nb[N] = kc · (√(f'c/1e6)) · (hef*1000)^1.5  with kc≈10 → convert consistently.
  const fcMPa = g.fc / 1e6;
  const hefMM = hef * 1000;
  const kc = 10; // cast-in, uncracked-adjacent default per ACI 17.4.2.2
  const Nb = kc * Math.sqrt(fcMPa) * Math.pow(hefMM, 1.5); // N (single anchor)

  // Full single-anchor cone plan area and the group's projected area.
  const ANco = 9 * hef * hef; // m^2
  // Group extent: bolt circle expanded by 1.5·hef on each side (a square proxy).
  const side = g.boltCircleDia + 3 * hef; // 1.5·hef each side
  let ANc = side * side; // m^2 (uncapped)
  // Edge cap: if bolts are near a concrete edge, the cone can't fully form.
  const edgeLimited = 2 * (g.edgeDistance + 1.5 * hef);
  if (g.edgeDistance < 1.5 * hef) {
    ANc = Math.min(ANc, edgeLimited * edgeLimited);
  }
  // Edge-distance modifier ψ_ed (ACI 17.4.2.5): 1.0 if ca_min ≥ 1.5·hef.
  const psiEd =
    g.edgeDistance >= 1.5 * hef ? 1.0 : 0.7 + (0.3 * g.edgeDistance) / (1.5 * hef);

  // Group breakout scales with the number of anchors sharing the cone, but the
  // ANc/ANco ratio already captures overlap; clamp ratio ≥ 1 (min one cone).
  const areaRatio = Math.max(1, ANc / ANco);
  return areaRatio * psiEd * Nb;
}

/**
 * BASE-PLATE bending capacity — the download/uplift that the plate can carry in
 * cantilever bending before it yields (N), nominal. The plate spans from the
 * pole wall out to the bolt circle; treat it as a cantilever of the bolt lever
 * arm `m`. Plastic moment per unit width Mp = Fy·t^2/4; over the plate width b:
 *   Mp_total = Fy · (b·t^2/4)     ;   F ≈ Mp_total / m
 * AASHTO/TowerNX rule of thumb: t_plate ≥ bolt Ø keeps this from governing. [TowerNX]
 */
export function basePlateBendingCapacity(g: AnchorBoltGroup): number {
  const Fy = BOLT_GRADES[g.plateGrade].Fy;
  const t = g.plateThickness;
  // effective plate width ≈ perimeter of the bolt circle shared by the bolts.
  const width = Math.PI * g.boltCircleDia;
  const Mp = Fy * (width * t * t) / 4; // N·m plastic moment of the plate strip
  // cantilever arm ≈ quarter of the bolt-circle diameter (pole wall → bolt).
  const arm = Math.max(0.02, g.boltCircleDia * 0.25);
  return Mp / arm;
}

/**
 * EMBEDDED-STUB PULLOUT (bond) capacity (N), nominal — a lattice leg cast into
 * concrete resisting axial load by bond over its surface, analogous to rebar
 * development:
 *   Qbond = u · (perimeter · embedment)
 * with ultimate bond stress u. ACI development uses u ≈ (1/4)·√f'c·(something);
 * we take a conservative u ≈ 0.6·√f'c (MPa) ≈ typical 2–3 MPa for 20–40 MPa
 * concrete, or the caller-supplied `bondStrength`. The stub must also not break
 * out a cone (compare with a hef^1.5 breakout term and take the min).
 */
export function stubPulloutCapacity(s: EmbeddedStub): number {
  const u = s.bondStrength ?? 0.6 * Math.sqrt(s.fc / 1e6) * 1e6; // Pa
  const bond = u * s.perimeter * s.embedment;

  // concrete breakout cone for a single deep embedment (ACI-style hef^1.5).
  const fcMPa = s.fc / 1e6;
  const hefMM = s.embedment * 1000;
  const Nb = 10 * Math.sqrt(fcMPa) * Math.pow(hefMM, 1.5); // N
  // edge reduction if shallow cover.
  const psiEd =
    s.edgeDistance >= 1.5 * s.embedment
      ? 1.0
      : Math.max(0.4, 0.7 + (0.3 * s.edgeDistance) / (1.5 * s.embedment));

  return Math.min(bond, psiEd * Nb);
}

/**
 * GUY-ANCHOR-ROD capacity (N), nominal — governed by the *lesser* of rod tensile
 * fracture (At·Fu) and the concrete/grout breakout of the block it's cast into.
 *   rod:      At · Fu        (At ≈ 0.75·A for a threaded rod)
 *   breakout: kc·√f'c·hef^1.5
 * The guy anchor is a single deep rod, so no group overlap term.
 */
export function guyAnchorRodCapacity(r: GuyAnchorRod): number {
  const A = (Math.PI / 4) * r.rodDia * r.rodDia;
  const At = 0.75 * A; // threaded tensile stress area ≈ 0.75·shank
  const rodFrac = At * BOLT_GRADES[r.grade].Fu;

  const fcMPa = r.fc / 1e6;
  const hefMM = r.embedment * 1000;
  const psiEd =
    r.edgeDistance >= 1.5 * r.embedment
      ? 1.0
      : Math.max(0.4, 0.7 + (0.3 * r.edgeDistance) / (1.5 * r.embedment));
  const breakout = psiEd * 10 * Math.sqrt(fcMPa) * Math.pow(hefMM, 1.5); // N

  return Math.min(rodFrac, breakout);
}

/**
 * REBAR-CAGE tension capacity (N), nominal — the vertical steel that ties the
 * anchor-bolt uplift into the pad:
 *   Ncage = As · fy = n · Ab · fy
 * Reduced by a development-efficiency factor if the provided development length
 * ld is short of what's needed (ACI 25.4 basic ld ≈ 40·db for typical bars):
 *   η = min(1, ld_provided / ld_required),  ld_required ≈ 40·db.
 */
export function rebarCageCapacity(c: RebarCage): number {
  const bar = REBAR_SIZES[c.bar];
  const fy = REBAR_GRADES[c.grade].fy;
  const As = c.nBars * bar.Ab;
  const ldRequired = 40 * bar.db; // ACI 25.4 simplified basic development length
  const eta = Math.max(0, Math.min(1, c.developmentLength / ldRequired));
  return As * fy * eta;
}

/**
 * Steel reinforcement ratio ρ = As / Ag of a rebar cage (dimensionless) — ACI
 * limits a compression member to 0.01 ≤ ρ ≤ 0.08. Reporting/validation only.
 */
export function rebarRatio(c: RebarCage): number {
  const bar = REBAR_SIZES[c.bar];
  return (c.nBars * bar.Ab) / c.grossArea;
}

// ---------------------------------------------------------------------------
// Resolved capacity bundle + utilization (what the solver consumes)
// ---------------------------------------------------------------------------

/**
 * Resolved *design* (φ-reduced) capacities for one connection, in N. `tension`
 * is the governing (minimum) uplift path — for an anchor group that's the least
 * of bolt tension, concrete breakout, plate bending, and (if a cage backs it)
 * the rebar tie. `shear` is the lateral capacity; `compression` the download the
 * plate/bearing can pass (∞ for embedded/rod types that don't take download).
 */
export interface ConnectionCapacity {
  tension: number; // N — governing uplift capacity (design, φ·Nn)
  shear: number; // N — governing shear capacity (design)
  compression: number; // N — download the connection passes into concrete
  governing: string; // human-readable name of the weakest mechanism
}

/** No-op / "make of steel" default when a base node has no explicit connection. */
export const RIGID_CONNECTION: ConnectionCapacity = {
  tension: Number.POSITIVE_INFINITY,
  shear: Number.POSITIVE_INFINITY,
  compression: Number.POSITIVE_INFINITY,
  governing: "rigid (uncapped)",
};

/**
 * Resolve a connection spec (optionally backed by a rebar cage) into design
 * capacities. This is the single entry the solver needs. `cage` is the pier
 * reinforcement that carries an anchor group's uplift into the pad; pass it for
 * anchor-bolt groups if you model the pier steel.
 */
export function connectionCapacity(
  spec: BaseConnectionSpec,
  cage?: RebarCage,
): ConnectionCapacity {
  switch (spec.kind) {
    case "anchorBolts": {
      const bolt = CONN_PHI.boltTension * anchorTensionCapacity(spec);
      const breakout = CONN_PHI.breakout * concreteBreakoutCapacity(spec);
      const plate = CONN_PHI.plate * basePlateBendingCapacity(spec);
      const shear = CONN_PHI.boltShear * anchorShearCapacity(spec);
      const cageCap = cage
        ? CONN_PHI.rebar * rebarCageCapacity(cage)
        : Number.POSITIVE_INFINITY;
      const paths: Array<[string, number]> = [
        ["anchor-bolt tension", bolt],
        ["concrete breakout", breakout],
        ["base-plate bending", plate],
        ["rebar-cage tie", cageCap],
      ];
      const [name, tension] = paths.reduce((a, b) => (b[1] < a[1] ? b : a));
      // download bears on concrete/grout: 0.85·f'c·plate area (ACI bearing).
      const compression = 0.65 * 0.85 * spec.fc * spec.platePlanArea;
      return { tension, shear, compression, governing: name };
    }
    case "embeddedStub": {
      const tension = CONN_PHI.bond * stubPulloutCapacity(spec);
      // an embedded stub carries shear by bearing on the concrete ≈ its bond too.
      const shear = tension * 0.6;
      // download: end bearing of the stub tip on concrete (small) + the same
      // bond in compression → treat as the same magnitude as pullout.
      return {
        tension,
        shear,
        compression: tension,
        governing: "embedded-stub bond/breakout",
      };
    }
    case "guyRod": {
      const tension = Math.min(
        CONN_PHI.boltTension * (0.75 * (Math.PI / 4) * spec.rodDia * spec.rodDia) *
          BOLT_GRADES[spec.grade].Fu,
        CONN_PHI.breakout * guyAnchorRodCapacity(spec),
      );
      // a guy rod is a pure-tension member: no meaningful shear/download.
      return {
        tension,
        shear: tension * 0.6,
        compression: 0,
        governing: "guy-anchor rod / breakout",
      };
    }
    case "rebarCage": {
      const tension = CONN_PHI.rebar * rebarCageCapacity(spec);
      return {
        tension,
        shear: tension * 0.6,
        compression: tension,
        governing: "rebar-cage tension",
      };
    }
  }
}

/**
 * UTILIZATION of a connection under a base reaction. Sign convention matches
 * foundation.ts: `verticalUp` is the uplift/tension demand (N, ≥0 when the leg
 * is being pulled up), `shear` the horizontal reaction magnitude, `verticalDown`
 * the compression download (N, ≥0). Returns the governing utilization across
 * tension / shear / (tension+shear interaction) / compression. ≥ 1 ⇒ the
 * connection fails and the node should be released (see docs/BASE_CONNECTION.md).
 *
 * Combined tension+shear uses the ACI 318 §17.6 elliptical interaction:
 *   (Nu/φNn)^(5/3) + (Vu/φVn)^(5/3) ≤ 1.
 */
export function connectionUtilization(
  cap: ConnectionCapacity,
  verticalUp: number,
  shear: number,
  verticalDown = 0,
): number {
  const sf = CONN_SAFETY_FACTOR;
  const nRatio = verticalUp > 0 && isFinite(cap.tension) ? (verticalUp * sf) / cap.tension : 0;
  const vRatio = shear !== 0 && isFinite(cap.shear) ? (Math.abs(shear) * sf) / cap.shear : 0;
  const cRatio =
    verticalDown > 0 && isFinite(cap.compression) ? (verticalDown * sf) / cap.compression : 0;
  // ACI elliptical tension+shear interaction expressed as an equivalent util.
  const interaction = Math.pow(
    Math.pow(Math.max(0, nRatio), 5 / 3) + Math.pow(Math.max(0, vRatio), 5 / 3),
    3 / 5,
  );
  return Math.max(nRatio, vRatio, cRatio, interaction);
}

// ---------------------------------------------------------------------------
// Defaults & preset builders — so existing towers don't regress
// ---------------------------------------------------------------------------

/**
 * Build a competent anchor-bolt group sized off a target bolt-circle diameter
 * and pole size. Defaults mirror the DS200 small-pole detail scaled up: enough
 * capacity that the built-in 30–60 m designs never lose at the connection unless
 * a player deliberately under-specs it. [DS200][NORWALK]
 */
export function defaultAnchorGroup(
  boltCircleDia = 0.6,
  fc = 20e6,
): AnchorBoltGroup {
  return {
    kind: "anchorBolts",
    nBolts: 8,
    bolt: "b1_25", // 1-1/4" rods — generous for game defaults
    grade: "f1554_55",
    boltCircleDia,
    embedment: 0.6, // ~24·db, comfortably deep
    edgeDistance: 0.3,
    platePlanArea: (Math.PI / 4) * (boltCircleDia + 0.15) * (boltCircleDia + 0.15),
    plateThickness: 0.038, // ≥ bolt Ø per AASHTO [TowerNX]
    plateGrade: "a36rod",
    fc,
  };
}

/** A default deadman guy-anchor rod (A449, deep hook) — strong so guys hold. */
export function defaultGuyRod(fc = 20e6): GuyAnchorRod {
  return {
    kind: "guyRod",
    rodDia: 0.032, // 32 mm eye rod
    grade: "a449",
    embedment: 0.9,
    edgeDistance: 0.5,
    fc,
  };
}

/** A default pier rebar cage (30 #8, Grade 60) matching the monopole drawings. */
export function defaultRebarCage(grossArea = Math.PI * 0.75 * 0.75): RebarCage {
  return {
    kind: "rebarCage",
    bar: "n8",
    nBars: 30, // [NORWALK 30 pier bars]
    grade: "g60",
    developmentLength: 1.2,
    grossArea,
  };
}
